import { useTranslation } from 'react-i18next';
import type { TurnRateLadderTier, VolumeDiscountSummary } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon, Seg } from '../ChatAssistant/compact/kit';
import { PLAN_ORDER, int, rate3, tierRange, usd, type PlanKey } from './model';
import { Caption, Modal, ModalHead, PrimaryBtn, TextBtn } from './ui';
import { ladderRows, pct, volumeOffCents, type LadderRow } from './volume';

interface Props {
  plan: PlanKey;
  onPlan: (p: PlanKey) => void;
  ladder: TurnRateLadderTier[];
  /** The viewer's Volume discount (ADR 0038); absent until the summary loads. */
  volume?: VolumeDiscountSummary;
  /** The list overage rate before any discount, in USD. */
  baseRateUsd?: number;
  noAssistUsd: number;
  wallet: number;
  onClose: () => void;
  onTopUp: () => void;
}

// Same order as the plan cards: the CRM, then the tools, then the AI assistant.
const FEATURES = ['crm', 'mass', 'priority', 'automated', 'ai'] as const;

export function ConditionsDialog({ plan, onPlan, ladder, volume, baseRateUsd, noAssistUsd, wallet, onClose, onTopUp }: Props) {
  const { t } = useTranslation('paymentsCompact');
  const prices = ladder.map((l) => l.plan_price_usd);
  const rates = ladder.map((l) => l.rate_usd);
  const lo = (v: number[]) => (v.length ? Math.min(...v) : 0);
  const hi = (v: number[]) => (v.length ? Math.max(...v) : 0);
  const span = (v: number[], fmt: (n: number) => string) => (lo(v) === hi(v) ? fmt(lo(v)) : fmt(lo(v)) + '–' + fmt(hi(v)));

  const priceLine: Record<PlanKey, string> = {
    assist: prices.length ? t('cond.price.assist', { range: span(prices, (n) => usd(n, 0)) }) : '',
    none: noAssistUsd ? t('cond.price.none', { price: usd(noAssistUsd, 0) }) : '',
    od: rates.length ? t('cond.price.od', { range: span(rates, rate3) }) : '',
  };

  // A figure we couldn't load stays as a raw {{placeholder}}; those bullets are dropped.
  const vars = {
    baseRate: baseRateUsd ? '$' + baseRateUsd.toFixed(6) : undefined,
    lowRate: rates.length ? rate3(lo(rates)) : undefined,
    noAssist: noAssistUsd ? usd(noAssistUsd, 0) : undefined,
  };
  const howRaw = t(`cond.how.${plan}`, { returnObjects: true, ...vars }) as unknown;
  const how = (Array.isArray(howRaw) ? (howRaw as string[]) : []).filter((s) => !s.includes('{{'));

  // Volume discount ladder: 1–9 → 0%, then each step; the last row is the negotiated 50+.
  const vrows = volume ? ladderRows(volume) : [];
  const steps = vrows.filter((r): r is LadderRow & { bps: number } => r.bps != null);
  const negotiated = vrows.find((r) => r.bps == null);
  const paidCount = volume?.paid_count ?? 0;
  // Full Subscription prices by earnings; No assistant has the one flat price.
  const cols = plan === 'none' ? (noAssistUsd ? [noAssistUsd] : []) : [...new Set(prices)].sort((a, b) => a - b);
  const mid = cols[Math.min(2, cols.length - 1)];
  const lastStep = steps[steps.length - 1];
  const after = (usdPrice: number, bps: number) => (usdPrice * 100 - volumeOffCents(usdPrice * 100, bps)) / 100;

  const tableHead = { display: 'grid', gap: 12, alignItems: 'center', height: 32, padding: '0 12px', background: C.input, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase' as const, color: C.muted };
  const tableRow = { display: 'grid', gap: 12, alignItems: 'center', height: 34, padding: '0 12px', borderTop: `1px solid ${C.divider}`, font: f(400, 12.5), fontVariantNumeric: 'tabular-nums' as const };
  const LAD_COLS = 'minmax(130px,1.3fr) 1fr 1fr 1fr';
  const DISC_COLS = `110px 90px repeat(${Math.max(1, cols.length)},minmax(0,1fr))`;
  const priceCell = (v: number) => usd(v, v % 1 ? 2 : 0);

  return (
    <Modal width={700} onClose={onClose}>
      <div style={{ position: 'sticky', top: 0, zIndex: 1, background: C.card, borderBottom: `1px solid ${C.border}`, paddingBottom: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <ModalHead icon="file-text" title={t('cond.title')} onClose={onClose} pad="14px 16px 0" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '0 16px' }}>
          <Seg<PlanKey> size="lg" value={plan} onChange={onPlan} options={PLAN_ORDER.map((k) => ({ id: k, label: t(`plan.${k}.name`) }))} />
          <span style={{ font: f(600, 13), color: C.purplePale }}>{priceLine[plan]}</span>
        </div>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <span style={{ font: f(400, 12.5, 1.5), color: C.sub }}>{t('cond.intro')}</span>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Caption>{t('cond.included')}</Caption>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '6px 16px' }}>
            {FEATURES.map((k) => {
              const yes = plan !== 'none' || k !== 'ai';
              return (
                <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, font: f(400, 13), color: yes ? 'var(--om-body)' : C.muted }}>
                  <Icon name={yes ? 'check' : 'x'} size={13} color={yes ? C.purpleLt : C.faint} />
                  {yes ? t(`cond.feature.${k}`) : t('plan.lines.noAi')}
                </span>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Caption>{t('cond.howTitle')}</Caption>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {how.map((h) => (
              <div key={h} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <span style={{ width: 4, height: 4, flex: 'none', marginTop: 8, borderRadius: 99, background: C.purple }} />
                <span style={{ font: f(400, 13, 1.5), color: 'var(--om-body)' }}>{h}</span>
              </div>
            ))}
          </div>
        </div>

        {plan !== 'none' && ladder.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Caption>{t('cond.ladderTitle')}</Caption>
            <span style={{ font: f(400, 12, 1.5), color: C.sub }}>{t('cond.ladderIntro')}</span>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
              <div style={{ minWidth: 460 }}>
                <div style={{ ...tableHead, gridTemplateColumns: LAD_COLS }}>
                  <span>{t('cond.col.earnings')}</span>
                  <span style={{ textAlign: 'right' }}>{t('plan.assist.name')}</span>
                  <span style={{ textAlign: 'right' }}>{t('plan.od.name')}</span>
                  <span style={{ textAlign: 'right' }}>{t('cond.col.breakEven')}</span>
                </div>
                {ladder.map((l) => (
                  <div key={l.min_gross} style={{ ...tableRow, gridTemplateColumns: LAD_COLS }}>
                    <span style={{ color: C.text }}>{tierRange(l, (v) => t('usage.under', { v }))}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600, color: plan === 'assist' ? C.purplePale : C.sub }}>{t('cond.perMo', { price: usd(l.plan_price_usd, 0) })}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600, color: plan === 'od' ? C.purplePale : C.sub }}>{rate3(l.rate_usd)}</span>
                    <span style={{ textAlign: 'right', color: C.sub }}>{t('cond.perMo', { price: '~' + int(l.crossover_turns) })}</span>
                  </div>
                ))}
              </div>
            </div>
            <span style={{ font: f(400, 12, 1.5), color: C.sub }}>{t('cond.breakEvenNote')} {plan === 'assist' ? t('cond.ladderNoteAssist') : t('cond.ladderNoteOd')}</span>
          </div>
        )}

        {plan !== 'od' && volume && volume.ladder.length > 0 && cols.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <Caption>{t('cond.discTitle')}</Caption>
              <span style={{ font: f(500, 11.5), color: C.purpleLt }}>
                {volume.source === 'override' ? t('volume.special', { pct: pct(volume.bps) }) : t('cond.youHave', { count: paidCount })}
              </span>
            </div>
            <span style={{ font: f(400, 12, 1.5), color: C.sub }}>{t('cond.discIntro')}</span>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
              <div style={{ minWidth: 560 }}>
                <div style={{ ...tableHead, gap: 10, gridTemplateColumns: DISC_COLS }}>
                  <span>{t('cond.col.accounts')}</span>
                  <span style={{ textAlign: 'right' }}>{t('cond.col.discount')}</span>
                  {cols.map((p) => <span key={p} style={{ textAlign: 'right' }}>{usd(p, 0)}</span>)}
                </div>
                {steps.map((s) => {
                  const on = s.current;
                  return (
                    <div key={s.min} style={{ ...tableRow, gap: 10, gridTemplateColumns: DISC_COLS, background: on ? 'color-mix(in srgb, var(--om-accent) 10%, transparent)' : 'transparent', color: on ? C.text : C.sub }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.text }}>
                        {s.label}
                        {on && <Icon name="check" size={12} color={C.purpleLt} />}
                      </span>
                      <span style={{ textAlign: 'right', fontWeight: 600, color: on ? C.purplePale : C.text }}>{s.bps ? pct(s.bps) : '—'}</span>
                      {cols.map((p) => <span key={p} style={{ textAlign: 'right' }}>{priceCell(after(p, s.bps))}</span>)}
                    </div>
                  );
                })}
                <div style={{ ...tableRow, gap: 10, gridTemplateColumns: DISC_COLS, background: negotiated?.current ? 'color-mix(in srgb, var(--om-accent) 10%, transparent)' : 'transparent', color: C.sub }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.text }}>
                    {negotiated?.label}
                    {negotiated?.current && <Icon name="check" size={12} color={C.purpleLt} />}
                  </span>
                  <span style={{ gridColumn: `2 / span ${cols.length + 1}`, textAlign: 'right' }}>{t('cond.discNegotiated')}</span>
                </div>
              </div>
            </div>
            {mid != null && lastStep && (
              <span style={{ font: f(400, 12, 1.5), color: C.sub }}>
                {t('cond.discExample', {
                  price: usd(mid, 0),
                  discounted: priceCell(after(mid, lastStep.bps)),
                  band: lastStep.label,
                })}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ position: 'sticky', bottom: 0, background: C.card, borderTop: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, font: f(400, 12), color: C.sub }}>{t('cond.wallet', { amount: usd(wallet) })}</span>
        <TextBtn onClick={onClose}>{t('close')}</TextBtn>
        <PrimaryBtn icon="plus" height={30} onClick={onTopUp} style={{ font: f(600, 12.5), padding: '0 14px' }}>{t('topUpWallet')}</PrimaryBtn>
      </div>
    </Modal>
  );
}
